<script setup>
import { computed } from 'vue';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import StatusBadge from 'dashboard/components/ui/StatusBadge.vue';
const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const referer = computed(() => props.conversationAttributes.referer);
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);
const whatsappReferral = computed(
  () => props.conversationAttributes.whatsapp_referral || {}
);
const whatsappAdHeadline = computed(() => whatsappReferral.value.headline);
const whatsappAdId = computed(() => whatsappReferral.value.source_id);
const whatsappAdUrl = computed(() => whatsappReferral.value.source_url);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const conversationStatus = computed(() => props.conversationAttributes.status);

const staticElements = computed(() =>
  [
    {
      content: conversationStatus,
      title: 'CONTACT_PANEL.CONVERSATION_STATUS',
      type: 'status_badge',
    },
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
    },
    {
      content: whatsappAdHeadline,
      title: 'CONTACT_PANEL.WHATSAPP_AD_HEADLINE',
    },
    {
      content: whatsappAdId,
      title: 'CONTACT_PANEL.WHATSAPP_AD_ID',
    },
    {
      content: whatsappAdUrl,
      title: 'CONTACT_PANEL.WHATSAPP_AD_URL',
      type: 'link',
      href: whatsappAdUrl.value,
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      type: 'link',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
    },
  ].filter(attribute => !!attribute.content.value)
);

const evenClass = [
  '[&>*:nth-child(odd)]:!bg-transparent [&>*:nth-child(even)]:!bg-surface-raised',
  'dark:[&>*:nth-child(odd)]:!bg-transparent dark:[&>*:nth-child(even)]:!bg-surface-raised',
];
</script>

<template>
  <div class="conversation--details">
    <div :class="evenClass">
      <ContactDetailsItem
        v-for="element in staticElements"
        :key="element.title"
        :title="$t(element.title)"
        :value="element.type === 'status_badge' ? '' : element.content.value"
        class="border-b border-solid border-line-faint"
      >
        <StatusBadge
          v-if="element.type === 'status_badge' && element.content.value"
          :status="element.content.value"
          size="small"
        />
        <a
          v-else-if="element.type === 'link'"
          :href="element.href || element.content.value"
          rel="noopener noreferrer nofollow"
          target="_blank"
          class="text-woot-400 dark:text-woot-600"
        >
          {{ element.content.value }}
        </a>
      </ContactDetailsItem>
    </div>
    <!-- Desde a #1243 esta lista não fica mais atrás de um bloco colapsável:
         conversa sem atributo nenhum deixaria o título "Informações da
         conversa" sozinho, parecendo tela quebrada. -->
    <CustomAttributes
      :start-at="staticElements.length % 2 === 0 ? 'even' : 'odd'"
      :empty-state-message="
        $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
      "
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
      panel
    />
  </div>
</template>
